import * as React from "react";
import { isNil } from "lodash";
import Media from "react-media";

import {
  ButtonProps,
  ButtonGroupProps,
  Button,
  HeaderProps,
  Header,
} from "../semantic-ui";

import {
  OTHER_COLOR,
  COLOR_ORDER,
  PIECE_UNICODE,
  encodeMove,
  countChallenges,
  default as GameState,
  Color,
  Coordinate,
} from "./GameState";

import styles from "./SpecialActions.module.css";

const OnlyAction = (props: ButtonProps) => (
  <Button style={{ width: "100%" }} {...props} />
);

const ActionGroup = ({ label, ...rest }: ButtonGroupProps) => (
  <Button.Group className={styles.actionGroup} {...rest} />
);

const Banner = (props: HeaderProps) => (
  <Header textAlign="center" size="small" {...props} />
);

type Props = {
  className?: string;
  game: GameState;
  nextAction?: "move" | "offer_duel" | "resolve_duel";
  pendingDuelBid?: number;
  // Which special controls are being rendered?
  color: Color;
  // If there is a move that needs a promotion, this is the partial UCI
  pendingPromotion?: string;
  selectedSquare?: Coordinate;
  onMove: (uci: string) => void;
  onStartDuel: (duelNumber: number, bid: number) => void;
  onSkipDuel: (duelNumber: number) => void;
  onResolveDuel: (bid: number, gain: boolean) => void;
};

type DuelStartState = {
  // If the player is submitting their challenge bid
  isChallenging: boolean;
};

class DuelStart extends React.Component<Props, DuelStartState> {
  state = { isChallenging: false };

  render() {
    const { game, pendingDuelBid } = this.props;
    const maxChallengeNumber = game.availableDuels
      ? Math.max(...game.availableDuels.map((d) => countChallenges(d)))
      : 0;
    const duelNumber = countChallenges(game.lastMove as any) + 1;
    if (this.state.isChallenging) {
      return this.renderDuelChallenge(duelNumber, maxChallengeNumber);
    }
    if (!isNil(pendingDuelBid)) {
      return (
        <ActionGroup>
          <Button disabled>Waiting for {OTHER_COLOR[this.props.color]}</Button>
          <Button onClick={() => this.setState({ isChallenging: true })}>
            Change bid
          </Button>
        </ActionGroup>
      );
    } else if (maxChallengeNumber === 1) {
      return (
        <ActionGroup>
          <Button onClick={() => this.setState({ isChallenging: true })}>
            Challenge
          </Button>
          <Button onClick={() => this.props.onSkipDuel(duelNumber)}>
            Decline
          </Button>
        </ActionGroup>
      );
    } else {
      return (
        <ActionGroup>
          <Button
            className={styles.shrinkButton}
            disabled
            children={`Challenge ${duelNumber} / ${maxChallengeNumber}`}
          />
          <Button onClick={() => this.setState({ isChallenging: true })}>
            Duel
          </Button>
          <Button onClick={() => this.props.onSkipDuel(duelNumber)}>
            Skip
          </Button>
        </ActionGroup>
      );
    }
  }

  renderDuelChallenge(
    duelNumber: number,
    maxChallengeNumber: number
  ): React.ReactNode {
    const { game } = this.props;
    return (
      <ActionGroup>
        <Button
          className={styles.shrinkButton}
          disabled
          children={
            maxChallengeNumber === 1
              ? "Challenge bid"
              : `Challenge #${duelNumber} bid`
          }
        />
        {game.availableChallenges().map((r) => (
          <Button key={r} onClick={() => this.handleStartDuel(duelNumber, r)}>
            {r}
          </Button>
        ))}
      </ActionGroup>
    );
  }

  handleStartDuel = (duel: number, bid: number) => {
    this.props.onStartDuel(duel, bid);
    this.setState({ isChallenging: false });
  };
}

class DuelResponse extends React.Component<Props> {
  render() {
    const { game } = this.props;
    const maxChallengeNumber = game.availableDuels
      ? Math.max(...game.availableDuels.map((d) => countChallenges(d)))
      : 0;
    const duelNumber = countChallenges(game.lastMove as any) + 1;
    const actions = [];
    for (const r of game.availableResponses()) {
      if (r === 0) {
        actions.push(
          <Button key="0+" onClick={() => this.props.onResolveDuel(0, true)}>
            0+
          </Button>
        );
        actions.push(
          <Button key="0-" onClick={() => this.props.onResolveDuel(0, false)}>
            0-
          </Button>
        );
      } else {
        actions.push(
          <Button key={r} onClick={() => this.props.onResolveDuel(r, false)}>
            {r}
          </Button>
        );
      }
    }
    return (
      <ActionGroup>
        <Button className={styles.shrinkButton} disabled>
          <Media queries={{ small: { maxWidth: 350 } }}>
            {(matches) => (
              <React.Fragment>
                {!matches.small ? "Respond to challenge" : ""}
                {maxChallengeNumber > 1
                  ? ` ${duelNumber} / ${maxChallengeNumber}`
                  : matches.small
                  ? "1/1"
                  : ""}
              </React.Fragment>
            )}
          </Media>
        </Button>
        {actions}
      </ActionGroup>
    );
  }
}

const isSelectingKing = (game: GameState, s: Coordinate | undefined) => {
  if (!s) return false;
  const piece = game.pieces[s[1]][s[0]];
  if (!piece) return false;
  return piece.toLowerCase() === "k";
};

const TwoKingsActions = ({ game, selectedSquare: s, onMove }: Props) => {
  if (isSelectingKing(game, s)) {
    const whirlwindUci = encodeMove(s as any, s as any);
    return (
      <OnlyAction
        disabled={game.legalMoves!.indexOf(whirlwindUci) === -1}
        onClick={() => onMove(whirlwindUci)}
      >
        Whirlwind Attack
      </OnlyAction>
    );
  } else if (game.kingTurn) {
    return (
      <OnlyAction onClick={() => onMove("0000")}>Skip King Turn</OnlyAction>
    );
  } else {
    return <Banner>Your move.</Banner>;
  }
};

const GameOver = ({ game, color }: Props) => (
  <Banner>
    {game.winner === "draw"
      ? "Game Over. Draw."
      : game.winner === color
      ? "You win!"
      : "Game over. You lose."}
  </Banner>
);

// Responsible for rendering the "special actions", covering:
// - Promotions
// - Initiating challenges
// - Responding to challenges
// - Passing king-turn
// - Whirlwind attack
const SpecialActions = (props: Props) => {
  const { game, nextAction, pendingPromotion, onMove, color } = props;

  if (!isNil(game.winner)) {
    return <GameOver {...props} />;
  } else if (nextAction === "resolve_duel") {
    if (game.lastMoveColor() === color) {
      return <DuelResponse {...props} />;
    } else {
      return <DuelStart {...props} />;
    }
  } else if (game.canChallenge()) {
    if (game.lastMoveColor() !== color) {
      return <DuelStart {...props} />;
    }
  } else if (game.toMove === color) {
    if (pendingPromotion) {
      const pending = pendingPromotion;
      return (
        <ActionGroup>
          <Button disabled>Promote to</Button>
          {["q", "r", "b", "n"].map((p) => (
            <Button key={p} onClick={() => onMove(`${pending}${p}`)}>
              {PIECE_UNICODE[p]}
            </Button>
          ))}
        </ActionGroup>
      );
    } else if (game.armies![COLOR_ORDER[color]] === "two_kings") {
      return <TwoKingsActions {...props} />;
    } else {
      return <Banner>Your move.</Banner>;
    }
  }

  return <Banner>Waiting for {OTHER_COLOR[color]}.</Banner>;
};

export default SpecialActions;
